import React, { useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Menu } from 'antd'
import { filter, isEmpty, isNil, map, values } from 'lodash'
import PropTypes from 'prop-types'

import { useAddDisabledFlagToElements } from '../../../account/hooks'
import { SopsNavLink } from '../../../sops/components/sections'
import { APP_ROUTES } from '../../constants'
import { filterTree, transformTree } from '../../helpers'
import { useFirstPathnameSegment } from '../../hooks'
import { StyledMenu, StyledNavigationBar } from '../styled'

import Logo from './logo'

const getItem = ({ label, key, disabled, icon, children }) => {
  if (isEmpty(children)) {
    return (
      <Menu.Item key={key} disabled={disabled}>
        {icon} {label}
      </Menu.Item>
    )
  }

  return (
    <Menu.SubMenu
      key={key}
      disabled={disabled}
      title={
        <Menu.Item style={{ padding: 0 }}>
          {icon} {label}
        </Menu.Item>
      }
    >
      {map(children, getItem)}
    </Menu.SubMenu>
  )
}

const recursivelyTransformChildrenToList = nodes =>
  map(nodes, node => {
    if (!isEmpty(node.children)) {
      return {
        ...node,
        children: recursivelyTransformChildrenToList(values(node.children)),
      }
    }

    return node
  })

const NavigationBar = ({ renderRightMenu }) => {
  const selectedKey = useFirstPathnameSegment()

  const navigationTabs = useMemo(
    () =>
      filter(
        recursivelyTransformChildrenToList(values(APP_ROUTES)),
        'isNavigationTab',
      ),
    [],
  )

  const navigationTabsOnPermissions =
    useAddDisabledFlagToElements(navigationTabs)

  const leftNavbarItems = useMemo(() => {
    const filteredBasedOnPermissionsTabs = filterTree(
      navigationTabsOnPermissions,
      ({ isNavigationTab, isHidden }) => isNavigationTab && !isHidden,
      () => true,
    )

    const mainNavigationPreparedItems = transformTree(
      filteredBasedOnPermissionsTabs,
      ({ fullPath, shouldOpenInNewTab, key, label, children, disabled }) => {
        let labelComponent = label
        if (!isNil(fullPath)) {
          if (fullPath === APP_ROUTES.MISC.children.SOPS.fullPath) {
            labelComponent = <SopsNavLink imitateDisabled={disabled} />
          } else {
            labelComponent = (
              <NavLink
                to={fullPath}
                target={shouldOpenInNewTab ? '_blank' : '_self'}
              >
                {label}
              </NavLink>
            )
          }
        }
        return { label: labelComponent, key, disabled, children }
      },
    )
    mainNavigationPreparedItems.unshift({
      label: <Logo />,
      key: 'logo',
      disabled: true,
    })

    return map(mainNavigationPreparedItems, getItem)
  }, [navigationTabsOnPermissions])

  const rightNavbarItems = useMemo(() => {
    const newRightNavbarItems = [
      getItem({
        icon: <UserOutlined />,
        key: 'user-sub-menu',
        children: [
          {
            label: (
              <NavLink to={APP_ROUTES.ACCOUNT.children.SETTINGS.fullPath}>
                {APP_ROUTES.ACCOUNT.children.SETTINGS.label}
              </NavLink>
            ),
            key: APP_ROUTES.ACCOUNT.children.SETTINGS.key,
            icon: <SettingOutlined />,
          },
          {
            label: (
              <NavLink to={APP_ROUTES.ACCOUNT.children.LOGOUT.fullPath}>
                {APP_ROUTES.ACCOUNT.children.LOGOUT.label}
              </NavLink>
            ),
            key: APP_ROUTES.ACCOUNT.children.LOGOUT.key,
            icon: <LogoutOutlined />,
          },
        ],
      }),
    ]

    if (renderRightMenu) {
      newRightNavbarItems.unshift(
        <Menu.Item key="rightMenu">{renderRightMenu()}</Menu.Item>,
      )
    }

    return newRightNavbarItems
  }, [renderRightMenu])

  return (
    <StyledNavigationBar>
      <StyledMenu mode="horizontal" selectedKeys={[selectedKey]}>
        {leftNavbarItems}
      </StyledMenu>
      <StyledMenu $end mode="horizontal" selectedKeys={[selectedKey]}>
        {rightNavbarItems}
      </StyledMenu>
    </StyledNavigationBar>
  )
}

NavigationBar.propTypes = {
  renderRightMenu: PropTypes.func,
}

NavigationBar.defaultProps = {
  renderRightMenu: undefined,
}

export default NavigationBar
